import { CornerDownLeft } from 'lucide-react';
import React, { useRef, useEffect } from 'react';
import { useAtom } from 'jotai';
import { t } from '~/services/i18n';
import { chatDraftMessage } from './index';

import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';

interface ChatBottombarProps {
    sendMessage: (newMessage: any) => boolean;
    waitForResponse: boolean;
}

export default function ChatBottombar({
    sendMessage,
    waitForResponse,
}: ChatBottombarProps) {
    const [message, setMessage] = useAtom(chatDraftMessage);
    const inputRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        // Focus the input when component mounts
        if (inputRef.current) {
            const timer = setTimeout(() => {
                inputRef.current?.focus();
            }, 150);
            return () => clearTimeout(timer);
        }
    }, []);

    const handleInputChange = (
        event: React.ChangeEvent<HTMLTextAreaElement>,
    ) => {
        setMessage(event.target.value);
    };

    const handleSend = () => {
        if (waitForResponse) {
            return;
        }

        if (message.trim()) {
            const success = sendMessage(message.trim());
            if (success) {
                setMessage('');
            }

            if (inputRef.current) {
                inputRef.current.focus();
            }
        }
    };

    const handleKeyPress = (
        event: React.KeyboardEvent<HTMLTextAreaElement>,
    ) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            handleSend();
        }

        if (event.key === 'Enter' && event.shiftKey) {
            event.preventDefault();
            setMessage((prev) => prev + '\n');
        }
    };

    return (
        <div className='flex flex-col p-1 m-4 mt-1 relative rounded-lg border bg-background focus-within:ring-1 focus-within:ring-ring'>
            <Label htmlFor='message' className='sr-only'>
                {t('chat.messageLabel', 'Message')}
            </Label>
            <Textarea
                autoComplete='off'
                value={message}
                ref={inputRef}
                onKeyDown={handleKeyPress}
                onChange={handleInputChange}
                name='q'
                placeholder={t(
                    'chat.messagePlaceholder',
                    'Type your message here...',
                )}
                className='min-h-8 text-md resize-none border-0 p-3 shadow-none focus-visible:ring-0'
            />
            <div className='flex items-center p-3 pt-0'>
                <Button
                    type='submit'
                    disabled={waitForResponse}
                    size='sm'
                    className='ml-auto gap-1.5'
                    onClick={handleSend}>
                    {waitForResponse
                        ? t(
                              'chat.waitingForResponse',
                              'Waiting for response...',
                          )
                        : t('chat.sendMessage', 'Send Message')}
                    <CornerDownLeft className='size-3.5' />
                </Button>
            </div>
        </div>
    );
}
